/**
 * Stage 4: finalising a court reply — sign it, and upload what was filed.
 *
 * Two acts, both of which already existed for every other document and were
 * hidden for these three:
 *
 * - **Save and sign** is a recorded act in the app (decision 33): who finalised
 *   the document and when. It is the same `mark-issued` command every document
 *   uses. From that moment the document is closed to edits; a change is a new
 *   draft, never a change to what was signed.
 * - **Upload the final version as filed** is the signed-copy upload. Counsel
 *   settles the reply in Word and files it, so what was filed is not what the
 *   app drafted. The uploaded copy supersedes the signed version — visibly,
 *   with the signed one kept in the history — and it is what the next court
 *   document is drafted against, because a rejoinder cites the paragraph
 *   numbers of the reply as filed.
 *
 * **A court-check finding never disables either control.** The checks by code
 * on a reply (a date, a reference, a judgment that is in no paper) report; the
 * officer decides. That was the owner's ruling, and it was narrow: it also took
 * one guard out altogether (`CR-CAT-33`, decision 34). It was **not** a ruling
 * that every block-level pre-issue rule on these documents is ignored.
 *
 * So Save and sign reads the version's `blocking` list — the server's set of
 * failing pre-issue rules, the same one the app's issue control reads for every
 * other document — and where it is not empty the button stays on screen,
 * greyed, with each rule and its words beside it (rule 3). Whether the act is
 * allowed is the server's answer; this screen holds no copy of the rule.
 *
 * **Upload the final version as filed is never gated by anything.** A filed
 * document exists; the app records it (stop list 5).
 *
 * **What signing does is said before the click, beside the button.** It signs
 * the newest version — which is not always the one open on the page — and it
 * takes a number from the register, which is never given back. Both were found
 * out afterwards on the first walk; neither is something to learn from a
 * result.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api/client'
import type {
  DocumentVersionView,
  CheckView,
  FinalCopyView,
  FinalisedView,
  MarkIssuedBody,
} from '../api/client'
import { readDay } from '../lib/blocks'
import { readDate } from '../lib/period'
import { ErrorEnvelope } from './bits'

/**
 * What Save and sign does, in one sentence, before the officer clicks.
 *
 * The newest version is the one signed, whichever is open — so where the page
 * is showing an older one, the sentence says which will be signed instead.
 * Until the list of versions has been read it names no version rather than
 * guess that the open one is the newest.
 */
export function signSays(open: number, versions: number[] | undefined): string {
  if (!versions?.length) {
    return 'Save and sign signs the newest version of this document and takes the next number from the register.'
  }
  const newest = Math.max(...versions)
  const which =
    newest === open
      ? `version ${open}, the one open on this page`
      : `version ${newest}, not version ${open} which is open on this page`
  return `Save and sign signs the newest version (${which}) and takes the next number from the register.`
}

export function FinaliseReplies({
  caseId,
  documentType,
  versionId,
  data,
  findings = 0,
  toConfirm = 0,
}: {
  caseId: string
  documentType: string
  versionId: string
  data: DocumentVersionView
  /** Replies a check did not pass. Said here; never a reason to grey a button. */
  findings?: number
  /** Replies with a passage for the office to confirm against the original. */
  toConfirm?: number
}) {
  const client = useQueryClient()
  const [signedOn, setSignedOn] = useState('')
  const [signedBy, setSignedBy] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [filedOn, setFiledOn] = useState('')
  const signed = Boolean(data.issued_on)
  const drafting = Boolean(data.drafting)

  // Which copy stands as the department's, as the server has it: the signing,
  // every final version uploaded since, and the sentence that says which of
  // them the next stage is drafted from. Read, never worked out here.
  const standing = useQuery({
    queryKey: ['finalised', caseId, documentType],
    queryFn: () => api.readFinalised(caseId, documentType),
    retry: false,
  })
  const final: FinalisedView | undefined = standing.data
  const uploads: FinalCopyView[] = final?.uploads ?? []

  // Signing signs the newest version, whichever is open. The list is the one
  // the page already holds; the newest is read off it, never assumed to be the
  // version on screen.
  const history = useQuery({
    queryKey: ['versions', caseId, documentType],
    queryFn: () => api.listVersions(caseId, documentType),
    enabled: !signed,
  })
  const signing = signSays(
    data.version,
    history.data?.map((row) => row.version),
  )

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['finalised', caseId, documentType] })
    void client.invalidateQueries({ queryKey: ['version', caseId, versionId] })
    void client.invalidateQueries({ queryKey: ['versions', caseId, documentType] })
    void client.invalidateQueries({ queryKey: ['case', caseId] })
    void client.invalidateQueries({ queryKey: ['papers', caseId] })
  }

  const sign = useMutation({
    mutationFn: () =>
      api.markIssued(caseId, {
        preflight: false,
        document_type: documentType,
        occurrence: data.occurrence,
        issued_on: signedOn,
        signed_by: signedBy.trim() || null,
      } satisfies MarkIssuedBody),
    onSuccess: refresh,
  })

  const upload = useMutation({
    mutationFn: () =>
      api.recordSignedCopy(
        caseId,
        documentType,
        versionId,
        file!,
        undefined,
        filedOn || undefined,
      ),
    onSuccess: () => {
      setFile(null)
      refresh()
    },
  })

  // The server's set of failing block-level pre-issue rules for this version.
  // Read, never recomputed, and never the court checks' findings — those stay
  // advisory, above.
  const blocking: CheckView[] = data.blocking ?? []
  const whyNotSign = drafting
    ? 'The replies are still being drafted. Sign when the drafting has finished.'
    : blocking.length
      ? `${blocking.length} pre-issue ${blocking.length === 1 ? 'check is' : 'checks are'} still failing, so this document cannot be signed yet. ${blocking.length === 1 ? 'It is' : 'Each is'} listed below with its rule.`
      : !signedOn
        ? 'Give the date it was signed.'
        : ''

  return (
    <section className="d18-finalise" aria-labelledby="finalise-title" data-testid="finalise">
      <h3 id="finalise-title">Finalise</h3>
      {final?.current_says ? (
        <p className="d18-finalised-says" role="status" data-testid="finalised-says">
          {final.current_says}
          {final.filed_on ? (
            <>
              {' '}
              Filed on <span className="mono">{readDate(final.filed_on)}</span>.
            </>
          ) : null}
        </p>
      ) : null}
      {findings ? (
        <p className="small muted">
          {findings} {findings === 1 ? 'reply has' : 'replies have'} something to check before
          filing. That is for you to weigh; it does not stop you signing or uploading.
        </p>
      ) : null}
      {toConfirm ? (
        <p className="small muted">
          {toConfirm} {toConfirm === 1 ? 'reply quotes' : 'replies quote'} a passage to confirm
          against the original. That does not stop you signing or uploading either.
        </p>
      ) : null}

      <div className="d18-finalise-act">
        <h4>Save and sign</h4>
        {signed ? (
          <p role="status" data-testid="signed-notice">
            Signed on <span className="mono">{readDate(data.issued_on)}</span>
            {final?.signed?.signed_by ? ` by ${final.signed.signed_by}` : ''}
            {final?.signed?.version ? ` · version ${final.signed.version}` : ''}
            {data.serial_no ? ` · serial ${data.serial_no}` : ''}. This document is closed to
            edits. To change it, draft another version; what was signed is kept.
          </p>
        ) : (
          <>
            <p className="small muted">
              Records that this document is final: who signed it and when. After signing the
              document is closed to edits.
            </p>
            <div className="row form">
              <label className="field">
                <span className="small muted">Signed by</span>
                <input
                  type="text"
                  aria-label="Signed by"
                  value={signedBy}
                  onChange={(event) => setSignedBy(event.target.value)}
                  placeholder="Name and designation"
                />
              </label>
              <label className="field">
                <span className="small muted">Date signed</span>
                <input
                  type="date"
                  className="mono"
                  aria-label="Date signed"
                  value={signedOn}
                  onChange={(event) => setSignedOn(event.target.value)}
                />
              </label>
            </div>
            {/* Beside the control, before the click: which version is signed,
                and that a register number is taken. */}
            <div className="d18-sign-act">
              <div className={whyNotSign ? 'blocked inline unsettled' : 'row'}>
                <button
                  type="button"
                  className="btn"
                  disabled={Boolean(whyNotSign) || sign.isPending}
                  onClick={() => sign.mutate()}
                  data-testid="save-and-sign"
                >
                  {sign.isPending ? 'Recording…' : 'Save and sign'}
                </button>
                {whyNotSign ? <span className="why">{whyNotSign}</span> : null}
              </div>
              {/* Each blocking rule, with its words — on screen, never hidden. */}
              {blocking.length ? (
                <ul className="d18-sign-blocking" data-testid="sign-blocking">
                  {blocking.map((check) => (
                    <li key={check.rule}>
                      <span className="mono small">{check.rule}</span> {check.text}
                      {check.citation ? (
                        <span className="small muted"> · {check.citation}</span>
                      ) : null}
                      {check.missing?.length ? (
                        <span className="small muted">
                          {' '}
                          · needs: {check.missing.join(', ')}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              <span className="small" data-testid="sign-says">
                {signing}
              </span>
            </div>
            <ErrorEnvelope error={sign.error} />
          </>
        )}
      </div>

      <div className="d18-finalise-act">
        <h4>Upload the final version as filed</h4>
        <p className="small muted">
          If the reply was settled outside the app, upload it as it was filed. It takes the
          place of the {signed ? 'signed version' : 'app’s draft'} and is what the next court
          document is drafted against. It is recorded against version {data.version}, the one
          open on this page. Nothing is deleted: the earlier version stays in the history.
          {blocking.length
            ? ' A failing pre-issue check does not stop this: a document that was filed is recorded.'
            : ''}
        </p>
        <div className="row form">
          <label className="field">
            <span className="small muted">The document as filed</span>
            <input
              type="file"
              accept="application/pdf,.pdf,.docx,image/*"
              aria-label="The final version as filed"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
                upload.reset()
              }}
            />
          </label>
          <label className="field">
            <span className="small muted">Date filed (optional)</span>
            <input
              type="date"
              className="mono"
              aria-label="Date filed"
              value={filedOn}
              onChange={(event) => setFiledOn(event.target.value)}
            />
          </label>
          <div className={file ? 'row' : 'blocked inline unsettled'}>
            <button
              type="button"
              className="quiet"
              disabled={!file || upload.isPending}
              onClick={() => upload.mutate()}
              data-testid="upload-final"
            >
              {upload.isPending ? 'Recording…' : 'Upload the final version'}
            </button>
            {!file ? <span className="why">Choose the filed document first.</span> : null}
          </div>
        </div>
        {upload.isSuccess ? (
          <p role="status" data-testid="final-recorded">
            The final version as filed is recorded.{' '}
            {signed
              ? 'It supersedes the version signed in the app, which is kept in the history.'
              : 'It stands in place of the app’s draft, which is kept in the history.'}{' '}
            The next court document will be drafted against it once it has been read.
          </p>
        ) : null}
        {uploads.length ? (
          <div data-testid="final-uploads">
            {final?.supersedes_signed ? (
              <p className="small">
                The uploaded final version supersedes the version signed in the app. The signed
                version is kept in the history. The next court document will be drafted against
                the uploaded one once it has been read.
              </p>
            ) : null}
            <ul className="court-files-list">
              {uploads.map((copy: FinalCopyView) => (
                <li key={copy.source_file_id}>
                  <a
                    className="mono"
                    href={api.sourceFilePath(caseId, copy.source_file_id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {copy.filename}
                  </a>
                  <span className="small muted">
                    {' '}
                    · uploaded {readDate(readDay(copy.uploaded_at))} ·{' '}
                    {copy.current
                      ? copy.read
                        ? 'the final version; it has been read'
                        : 'the final version; it is still being read, and until then the next court document is drafted from the earlier one'
                      : 'replaced by a later upload; kept on the file'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <ErrorEnvelope error={upload.error} />
      </div>
    </section>
  )
}
